/**
 * IP Intel Report — the page's fixed copy, verbatim from the design
 * (Main.dc.html). Prices are NOT here: every price string comes from
 * GET /api/v1/ip-intel/config.
 */

import type { IpIntelSample } from './api';

export const CONTACT_EMAIL = 'info@universitytech.io';

export const QUESTIONS: { n: string; q: string; module: string }[] = [
  { n: '01', q: 'How big is the opportunity — and does the number hold up two ways?', module: 'Market Sizing: Bottom-Up vs. Top-Down' },
  { n: '02', q: 'What should we ask for, and how should the deal be structured?', module: 'Royalty Benchmarking & Deal Structure' },
  { n: '03', q: 'Where is this technology worth the most, and how soon does it pay?', module: 'Technology Profiling & Use Cases' },
  { n: '04', q: 'Who already solves this problem, and can we actually beat them?', module: 'Competitive Landscape' },
  { n: '05', q: 'Is the IP defensible, blocked, or sitting on open white space?', module: 'IP Landscape' },
  { n: '06', q: 'What does the next proof point cost, and who pays for it?', module: 'Funding Pathway' },
  { n: '07', q: 'Licence, spin out or partner — and what do we do in the next 90 days?', module: 'GTM Recommendation' },
];

/** Logos under Frontend/public/ip-intel/logos/ (owner's files, 2026-10-06,
 *  resized to 240 px; the SRM one is the vector original). */
export const TRUSTED_BY: { name: string; logo: string }[] = [
  { name: 'IIT Bombay', logo: '/ip-intel/logos/iit-bombay.png' },
  { name: 'IIT Indore', logo: '/ip-intel/logos/iit-indore.png' },
  { name: 'SRM University', logo: '/ip-intel/logos/srm-ist.svg' },
  { name: 'VIT Vellore', logo: '/ip-intel/logos/vit-vellore.png' },
  { name: 'Ruhi Labs', logo: '/ip-intel/logos/ruhi-labs.png' },
  { name: 'Tata Institute for Genetics and Society', logo: '/ip-intel/logos/tigs.jpg' },
];

/** The industry tag per built-in sample, for a server that predates the
 *  admin-editable `industry` field (it then sends none). */
export const SAMPLE_INDUSTRY: Record<string, string> = {
  electrolyte: 'Healthcare & Life Sciences',
  geopolymer: 'Construction & Smart Cities',
  polyherbal: 'Healthcare & Life Sciences',
  vitaminb12: 'Agriculture & Food',
};

/** Shown until /config answers (and if it never does), so the section is
 *  never empty. The server's list wins once it arrives. */
export const FALLBACK_SAMPLES: IpIntelSample[] = [
  {
    id: 'electrolyte',
    title: 'Portable Electrolyte Detection Device',
    org: 'IIT Bombay',
    detail: 'Point-of-care Na⁺ / K⁺ sensing device · commercialization and patent IP evaluation.',
    industry: 'Healthcare & Life Sciences',
    cover_url: '/ip-intel/covers/electrolyte.png',
  },
  {
    id: 'geopolymer',
    title: 'High-Strength Geopolymer Composition (HSGPC) for Cement-Free Concrete',
    org: 'IIT Indore · Patent 585523 (granted)',
    detail: 'Cement-free binder, no curing water, roughly 80% lower embodied CO₂.',
    industry: 'Construction & Smart Cities',
    cover_url: '/ip-intel/covers/geopolymer.png',
  },
  {
    id: 'polyherbal',
    title: 'Polyherbal Formulation for Type 2 Diabetes Management',
    org: 'SRM University · Patent 572814 (granted)',
    detail: 'Seven-botanical formulation for T2D management, with a three-track go-to-market.',
    industry: 'Healthcare & Life Sciences',
    cover_url: '/ip-intel/covers/polyherbal.png',
  },
  {
    id: 'vitaminb12',
    title: 'Natural Vitamin B₁₂ Fortification in Fermented Foods',
    org: 'VIT Vellore · Patent 202441015300',
    detail: 'Fermentation-derived B₁₂ fortification, with a four-track go-to-market.',
    industry: 'Agriculture & Food',
    cover_url: '/ip-intel/covers/vitaminb12.png',
  },
];

export const FAQS: { q: string; a: string }[] = [
  {
    q: 'What do I have to provide?',
    a: 'Either a Cosmos technology listing or your own IP — patent, application, disclosure or dossier. TRL and stage of development are compulsory inputs in both cases.',
  },
  {
    q: 'How long does it take?',
    a: 'Seven days from a complete submission, subject to input clarifications. If we need to come back to you for detail, the clock pauses until we have it.',
  },
  {
    q: 'Can one report cover several technologies?',
    a: 'Not in a single report — the market, royalty and IP analysis stays specific enough to negotiate on. Have several technologies to assess? Contact us for bulk pricing.',
  },
  {
    q: 'Is this a legal opinion?',
    a: 'No. The IP Landscape module is commercial analysis of the patent terrain — useful for flagging risk and directing counsel, not a substitute for a formal freedom-to-operate opinion.',
  },
];

/** One-line TRL definitions for the intake select (standard NASA/EU scale). */
export const TRL_LEVELS: { n: number; label: string }[] = [
  { n: 1, label: 'Basic principles observed' },
  { n: 2, label: 'Technology concept formulated' },
  { n: 3, label: 'Experimental proof of concept' },
  { n: 4, label: 'Technology validated in the lab' },
  { n: 5, label: 'Technology validated in a relevant environment' },
  { n: 6, label: 'Technology demonstrated in a relevant environment' },
  { n: 7, label: 'System prototype demonstrated in an operational environment' },
  { n: 8, label: 'System complete and qualified' },
  { n: 9, label: 'Actual system proven in an operational environment' },
];

export const STAGES = [
  'Concept / idea',
  'Proof of concept',
  'Lab prototype',
  'Validated in lab',
  'Validated in relevant environment',
  'Pilot / demonstrated',
  'Market-ready',
];

/** Intake upload limits — mirrored by the server, which is the real gate. */
export const UPLOAD_EXTS = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'zip'];
export const UPLOAD_MAX_FILES = 5;
export const UPLOAD_MAX_MB = 25;

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/** 15-character Indian GSTIN: 2-digit state, PAN, entity no., 'Z', checksum. */
export const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
